import type {
  Media,
  Variant,
} from '../../../lib/flotiq-client/types/api-response';
import type { UseAppForm } from '../../form';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { useModals } from '../../../contexts/ModalContext';

import VariantModalForm from '../../../form/VariantModalForm/VariantModalForm';
import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';

interface VariantsFieldProps {
  /**
   * User data
   */
  media: Media;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * Page test id
   */
  testId?: string;
}

const VariantsField = ({
  media,
  disabled,
  testId = '',
}: VariantsFieldProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<Variant[]>();
  const yjsField = useYjsField('array');

  const { t } = useTranslation();
  const modal = useModals();

  const variants = useMemo(() => field.state.value || [], [field.state.value]);

  const variantsNames = useMemo(
    () => (variants || []).map((variant) => variant?.name),
    [variants],
  );

  const onBlur = () => {
    field.handleBlur();
    yjsField.onBlur();
  };

  const addNewVariant = (variant: Variant) => {
    field.pushValue(variant);
    yjsField.insert(variant);
    onBlur();
  };

  const updateVariant = useCallback(
    (newVariant: Variant, idx: number) => {
      field.replaceValue(idx, newVariant);
      yjsField.replace(idx, newVariant);
    },
    [field, yjsField],
  );

  const openVariantModal = async (
    oldVariant?: Variant | undefined,
    idx?: number,
    errors?: any,
  ) => {
    const newVariant = await modal({
      title: t('MediaEdit.AddVariant'),
      content: (
        <VariantModalForm
          variant={oldVariant}
          media={media}
          variantsNames={variantsNames as any}
          errors={errors}
          {...getTestProps(testId, 'modal', 'testId')}
        />
      ),
      size: 'xl',
      dialogAdditionalClasses: 'max-h-screen',
    });

    if (newVariant) {
      if (oldVariant && typeof idx === 'number') updateVariant(newVariant, idx);
      else addNewVariant(newVariant);
    }
  };

  const deleteVariant = async (idx: number) => {
    const result = await modal.delete(t('ContentForm.RemoveItem'));
    if (!result) return;
    field.removeValue(idx);
    yjsField.delete(idx);
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => openVariantModal()}
        disabled={disabled}
        {...getTestProps(testId, 'add', 'testId')}
      >
        {t('MediaEdit.AddVariant')}
      </Button>
      <div
        className={twMerge(
          'gap-2 sm:gap-4 grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))]',
          'md:grid-cols-[repeat(auto-fill,minmax(16rem,1fr))]',
          variants.length > 0 && 'mt-4',
        )}
      >
        {variants.map((_, idx) => {
          const fieldName = `${field.name}[${idx}]`;
          return (
            <form.AppField key={fieldName} name={fieldName}>
              {(subField) => (
                <subField.VariantField
                  media={media}
                  openVariantModal={openVariantModal}
                  deleteVariant={deleteVariant}
                  idx={idx}
                  disabled={disabled}
                  testId={testId}
                />
              )}
            </form.AppField>
          );
        })}
      </div>
    </>
  );
};

export default VariantsField;
